import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, X, ExternalLink } from 'lucide-react';
import { useAuth } from '../../App';
import { getProductsAdmin, createProduct, updateProduct, deleteProduct, toggleSale, toggleVisibility } from '../../api/products';
import { API } from '../../api/client';
import AdminLayout from './AdminLayout';

// Extract Google Drive file ID from any Drive or proxy URL
const extractDriveFileId = (url) => {
  if (!url) return null;
  const patterns = [
    /\/proxy-image\/([a-zA-Z0-9_-]+)/,  // our proxy URL
    /\/file\/d\/([a-zA-Z0-9_-]+)/,
    /\/d\/([a-zA-Z0-9_-]+)/,
    /[?&]id=([a-zA-Z0-9_-]+)/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// For saving to DB: always store as standard Drive URL (so backend can extract file_id)
const toGDriveViewUrl = (url) => {
  if (!url) return url;
  const fileId = extractDriveFileId(url);
  if (fileId) return `https://drive.google.com/uc?export=view&id=${fileId}`;
  return url;
};

// For displaying in <img> tags: use proxy URL
const toDisplayUrl = (url) => {
  if (!url) return url;
  const fileId = extractDriveFileId(url);
  if (fileId && BACKEND_URL) return `${BACKEND_URL}/api/proxy-image/${fileId}`;
  if (fileId) return `https://drive.google.com/uc?export=view&id=${fileId}`;
  return url;
};

// Convert any Google Drive share URL to download URL
const toGDriveDownloadUrl = (url) => {
  if (!url) return url;
  const allowedDomains = ['drive.google.com', 'docs.google.com', 'lh3.googleusercontent.com', 'drive.usercontent.google.com'];
  if (!allowedDomains.some(d => url.includes(d))) return url;
  const patterns = [
    /\/file\/d\/([a-zA-Z0-9_-]+)/,
    /\/d\/([a-zA-Z0-9_-]+)/,
    /[?&]id=([a-zA-Z0-9_-]+)/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return `https://drive.usercontent.google.com/download?id=${m[1]}&export=download&authuser=0&confirm=t`;
  }
  return url;
};

const AdminProducts = () => {
  const { token } = useAuth();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editProduct, setEditProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: '', subtitle: '', description: '', short_description: '', regular_price: 129, sale_price: 79,
    is_on_sale: true, is_bundle: false, pdf_url: '', audio_key: '', cover_image: '', badge: 'E-BOOK', benefits: ['', ''],
    bundle_items: [{ name: '', pdf_url: '', audio_key: '', cover_image: '' }]
  });

  const fetchProducts = useCallback(async () => {
    try {
      const data = await getProductsAdmin(token);
      setProducts(data);
    } catch (err) {} finally { setLoading(false); }
  }, [token]);

  useEffect(() => { if (token) fetchProducts(); }, [token, fetchProducts]);

  const openModal = (product = null) => {
    if (product) {
      setEditProduct(product);
      setFormData({
        name: product.name, subtitle: product.subtitle || '', description: product.description, short_description: product.short_description,
        regular_price: product.regular_price, sale_price: product.sale_price, is_on_sale: product.is_on_sale,
        is_bundle: product.is_bundle, pdf_url: product.pdf_url, audio_key: product.audio_key || '', cover_image: product.cover_image,
        badge: product.badge, benefits: product.benefits?.length ? product.benefits : ['', ''],
        bundle_items: product.bundle_items?.length ? product.bundle_items : [{ name: '', pdf_url: '', cover_image: '' }]
      });
    } else {
      setEditProduct(null);
      setFormData({ name: '', subtitle: '', description: '', short_description: '', regular_price: 129, sale_price: 79,
        is_on_sale: true, is_bundle: false, pdf_url: '', audio_key: '', cover_image: '', badge: 'E-BOOK', benefits: ['', ''],
        bundle_items: [{ name: '', pdf_url: '', audio_key: '', cover_image: '' }] });
    }
    setModalOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const data = {
      ...formData,
      cover_image: toGDriveViewUrl(formData.cover_image),
      pdf_url: toGDriveDownloadUrl(formData.pdf_url),
      benefits: formData.benefits.filter(b => b.trim())
    };
    try {
      if (editProduct) {
        await updateProduct(token, editProduct.id, data);
      } else {
        await createProduct(token, data);
      }
      setModalOpen(false);
      fetchProducts();
    } catch (err) { alert('Błąd zapisu'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Czy na pewno usunąć produkt?')) return;
    try {
      await deleteProduct(token, id);
      fetchProducts();
    } catch (err) { alert('Błąd usuwania'); }
  };

  const handleToggleSale = async (id) => {
    try { await toggleSale(token, id); fetchProducts(); } catch {}
  };

  const handleToggleVisibility = async (id) => {
    try { await toggleVisibility(token, id); fetchProducts(); } catch {}
  };

  return (
    <AdminLayout title="Produkty">
      <div className="flex justify-between items-center mb-6">
        <p className="text-[#9CA3AF]">{products.length} produktów</p>
        <button onClick={() => openModal()} className="btn-primary" data-testid="add-product-btn">
          <Plus size={18} /> Dodaj produkt
        </button>
      </div>

      {loading ? <div className="flex justify-center py-12"><div className="spinner"></div></div> : (
        <div className="glass-card overflow-hidden">
          <div className="overflow-x-auto">
            <table className="table-custom">
              <thead>
                <tr>
                  <th>Produkt</th>
                  <th>Cena reg.</th>
                  <th>Cena promo</th>
                  <th>Promocja</th>
                  <th>Widoczny</th>
                  <th>Sprzedano</th>
                  <th>Akcje</th>
                </tr>
              </thead>
              <tbody>
                {products.map((p) => (
                  <tr key={p.id} className={p.is_active === false ? 'opacity-50' : ''} data-testid={`product-row-${p.id}`}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-12 bg-[#10b981]/10 rounded flex items-center justify-center text-[#10b981] text-xs overflow-hidden">
                          {p.cover_image ? (
                            <img
                              src={toDisplayUrl(p.cover_image)}
                              alt=""
                              className="w-full h-full object-cover rounded"
                              onError={(e) => { e.target.style.display = 'none'; }}
                            />
                          ) : 'PDF'}
                        </div>
                        <div>
                          <p className="text-white font-medium">{p.name}</p>
                          {p.subtitle && <p className="text-xs text-[#9CA3AF]">{p.subtitle}</p>}
                          <span className="text-xs text-[#6B7280]">{p.badge}</span>
                        </div>
                      </div>
                    </td>
                    <td className="text-[#9CA3AF]">{p.regular_price} zł</td>
                    <td className="text-[#10b981] font-medium">{p.sale_price} zł</td>
                    <td>
                      <button onClick={() => handleToggleSale(p.id)} className={`toggle-switch ${p.is_on_sale ? 'active' : ''}`} data-testid={`toggle-sale-${p.id}`}></button>
                    </td>
                    <td>
                      <button onClick={() => handleToggleVisibility(p.id)} className={`toggle-switch ${p.is_active !== false ? 'active' : ''}`} data-testid={`toggle-visibility-${p.id}`}></button>
                    </td>
                    <td className="text-[#9CA3AF]">{p.sold_count || 0}</td>
                    <td>
                      <div className="flex gap-2">
                        <button onClick={() => openModal(p)} className="p-2 text-[#9CA3AF] hover:text-[#10b981] transition-colors" data-testid={`edit-product-${p.id}`}>
                          <Edit size={18} />
                        </button>
                        <button onClick={() => handleDelete(p.id)} className="p-2 text-[#9CA3AF] hover:text-red-400 transition-colors" data-testid={`delete-product-${p.id}`}>
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <div className="modal-overlay" onClick={() => setModalOpen(false)}>
          <div className="modal-content max-w-2xl" onClick={e => e.stopPropagation()}>
            <div className="flex justify-between items-center p-6 border-b border-white/5">
              <h2 className="text-xl font-bold text-white">{editProduct ? 'Edytuj produkt' : 'Dodaj produkt'}</h2>
              <button onClick={() => setModalOpen(false)} className="text-[#6B7280] hover:text-white"><X size={24} /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-4 max-h-[70vh] overflow-y-auto">
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Główny tytuł *</label>
                <input type="text" placeholder="np. Jak budować relację, która działa" value={formData.name} onChange={e => setFormData({...formData, name: e.target.value})} className="w-full" required />
              </div>
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Podtytuł <span className="text-[#6B7280]">(druga linia)</span></label>
                <input type="text" placeholder="np. Plan od pierwszych rozmów do bliskości" value={formData.subtitle} onChange={e => setFormData({...formData, subtitle: e.target.value})} className="w-full" />
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#9CA3AF] mb-2">Cena regularna (zł)</label>
                  <input type="number" value={formData.regular_price} onChange={e => setFormData({...formData, regular_price: parseFloat(e.target.value)})} className="w-full" />
                </div>
                <div>
                  <label className="block text-sm text-[#9CA3AF] mb-2">Cena promocyjna (zł)</label>
                  <input type="number" value={formData.sale_price} onChange={e => setFormData({...formData, sale_price: parseFloat(e.target.value)})} className="w-full" />
                </div>
              </div>
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Krótki opis</label>
                <input type="text" value={formData.short_description} onChange={e => setFormData({...formData, short_description: e.target.value})} className="w-full" />
              </div>
              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Opis</label>
                <textarea value={formData.description} onChange={e => setFormData({...formData, description: e.target.value})} className="w-full" rows={3} />
              </div>

              {/* Google Drive URLs */}
              <div className="p-4 bg-[#0B0F0E] border border-[#10b981]/20 rounded-lg">
                <div className="flex items-center gap-2 mb-3">
                  <ExternalLink size={16} className="text-[#10b981]" />
                  <span className="text-sm font-medium text-[#10b981]">Google Drive</span>
                  <span className="text-xs text-[#6B7280]">— udostępnij plik → Kopiuj link → wklej poniżej</span>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm text-[#9CA3AF] mb-2">Okładka (link Google Drive)</label>
                    <input
                      type="text"
                      value={formData.cover_image}
                      onChange={e => setFormData({...formData, cover_image: e.target.value})}
                      className="w-full text-sm"
                      placeholder="https://drive.google.com/file/d/..."
                    />
                    {formData.cover_image && (
                      <div className="mt-2">
                        <img
                          src={toDisplayUrl(formData.cover_image)}
                          alt="Podgląd okładki"
                          className="h-20 rounded border border-white/10"
                          onError={(e) => { e.target.style.display = 'none'; }}
                        />
                      </div>
                    )}
                  </div>
                  {!formData.is_bundle && (
                    <div>
                      <label className="block text-sm text-[#9CA3AF] mb-2">E-book PDF (link Google Drive)</label>
                      <input
                        type="text"
                        value={formData.pdf_url}
                        onChange={e => setFormData({...formData, pdf_url: e.target.value})}
                        className="w-full text-sm"
                        placeholder="https://drive.google.com/file/d/..."
                      />
                      {formData.pdf_url && (
                        <p className="text-xs text-[#10b981] mt-1">✓ Link ustawiony</p>
                      )}
                    </div>
                  )}
                  {!formData.is_bundle && (
                    <div>
                      <label className="block text-sm text-[#9CA3AF] mb-2">Audiobook — nazwa pliku w R2</label>
                      <input
                        type="text"
                        value={formData.audio_key}
                        onChange={e => setFormData({...formData, audio_key: e.target.value})}
                        className="w-full text-sm"
                        placeholder="AudioBook_Jak_budować_relację.mp3"
                      />
                      {formData.audio_key && (
                        <p className="text-xs text-[#10b981] mt-1">✓ Audio ustawione (Cloudflare R2)</p>
                      )}
                    </div>
                  )}
                </div>
                <p className="text-xs text-[#6B7280] mt-3">
                  Ustawienia udostępniania: <strong className="text-[#9CA3AF]">Ogólny dostęp → Każdy, kto ma link → Przeglądający</strong>
                </p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm text-[#9CA3AF] mb-2">Badge</label>
                  <input type="text" value={formData.badge} onChange={e => setFormData({...formData, badge: e.target.value})} className="w-full" />
                </div>
                <div className="flex items-center gap-4 pt-6">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.is_on_sale} onChange={e => setFormData({...formData, is_on_sale: e.target.checked})} className="checkbox-custom" />
                    <span className="text-white">Promocja ON</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={formData.is_bundle} onChange={e => setFormData({...formData, is_bundle: e.target.checked})} className="checkbox-custom" />
                    <span className="text-white">Bundle</span>
                  </label>
                </div>
              </div>

              {/* Bundle items */}
              {formData.is_bundle && (
                <div>
                  <label className="block text-sm text-[#9CA3AF] mb-3">E-booki w bundle</label>
                  <div className="space-y-4">
                    {formData.bundle_items.map((item, i) => (
                      <div key={i} className="p-3 border border-white/10 rounded-lg space-y-2 relative">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-xs text-[#6B7280]">E-book #{i + 1}</span>
                          {formData.bundle_items.length > 1 && (
                            <button type="button" onClick={() => {
                              const items = formData.bundle_items.filter((_, idx) => idx !== i);
                              setFormData({...formData, bundle_items: items});
                            }} className="text-red-400 hover:text-red-300 text-xs">✕ Usuń</button>
                          )}
                        </div>
                        <input type="text" placeholder="Nazwa e-booka" value={item.name}
                          onChange={e => {
                            const items = [...formData.bundle_items];
                            items[i] = {...items[i], name: e.target.value};
                            setFormData({...formData, bundle_items: items});
                          }} className="w-full" />
                        <input type="text" placeholder="Link PDF (Google Drive)" value={item.pdf_url}
                          onChange={e => {
                            const items = [...formData.bundle_items];
                            items[i] = {...items[i], pdf_url: toGDriveDownloadUrl(e.target.value)};
                            setFormData({...formData, bundle_items: items});
                          }} className="w-full" />
                        <input type="text" placeholder="Audiobook — nazwa pliku w R2 (opcjonalnie)" value={item.audio_key || ''}
                          onChange={e => {
                            const items = [...formData.bundle_items];
                            items[i] = {...items[i], audio_key: e.target.value};
                            setFormData({...formData, bundle_items: items});
                          }} className="w-full" />
                        <input type="text" placeholder="Obładka (Google Drive) — opcjonalnie" value={item.cover_image}
                          onChange={e => {
                            const items = [...formData.bundle_items];
                            items[i] = {...items[i], cover_image: toGDriveViewUrl(e.target.value)};
                            setFormData({...formData, bundle_items: items});
                          }} className="w-full" />
                      </div>
                    ))}
                  </div>
                  <button type="button" onClick={() => setFormData({...formData, bundle_items: [...formData.bundle_items, {name: '', pdf_url: '', audio_key: '', cover_image: ''}]})}
                    className="mt-3 text-[#10b981] text-sm">+ E-book PDF (link Google Drive)</button>
                </div>
              )}

              <div>
                <label className="block text-sm text-[#9CA3AF] mb-2">Korzyści (po 1 na linię)</label>
                {formData.benefits.map((b, i) => (
                  <input key={i} type="text" value={b} onChange={e => {
                    const nb = [...formData.benefits];
                    nb[i] = e.target.value;
                    setFormData({...formData, benefits: nb});
                  }} className="w-full mb-2" placeholder={`Korzyść ${i+1}`} />
                ))}
                <button type="button" onClick={() => setFormData({...formData, benefits: [...formData.benefits, '']})} className="text-[#10b981] text-sm">+ Dodaj korzyść</button>
              </div>
              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setModalOpen(false)} className="btn-ghost flex-1">Anuluj</button>
                <button type="submit" className="btn-primary flex-1">{editProduct ? 'Zapisz zmiany' : 'Dodaj produkt'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminProducts;
